<template>
  <div class="comments-container">
    <ConfirmationDialog
      ref="confirmDialogDeleteComment"
      :title="$t('components.sidebar.TheComments.confirmation.title')"
      :message="$t('components.sidebar.TheComments.confirmation.message')"
      :confirm-text="$t('components.sidebar.TheComments.confirmation.delete')"
      :cancel-text="$t('components.sidebar.TheComments.confirmation.cancel')"
    />
    <div
      v-if="loadingComments"
      class="d-flex align-items-center justify-content-center mt-5"
    >
      <i
        class="far fa-3x fa-spinner fa-spin"
        style="--fa-animation-duration: 1.5s;"
      />
    </div>

    <div v-else>
      <div class="comment-input">
        <textarea
          v-model="newComment"
          :placeholder="$t('components.sidebar.TheComments.commentPlaceholder')"
        />
        <div v-if="isValidator && !hasAlreadyValidated">
          <LocationValidation @validation="(validation) => processValidation(validation)" />
        </div>
        <button
          :disabled="!newComment.trim() ||
            (!newComment.trim() && validation === 'rejected')
          "
          @click="submitComment"
        >
          <b-spinner
            v-if="submittingComment"
            small
          />

          <span v-else>{{ $t("components.sidebar.TheComments.post") }}</span>
        </button>
      </div>

      <div v-if="comments.length">
        <div
          v-for="comment in comments"
          :key="comment.uuid"
          class="comment-item"
        >
          <div class="comment-header">
            <strong>
              {{ comment.type === "system" ? "System" : comment.user?.name }}
            </strong>
            <span class="comment-date">{{
              formatDate(comment.updated_at ?? comment.created_at)
            }}</span>
            <b-spinner
              v-if="deletingCommentId === comment.uuid"
              small
            />
          </div>

          <div
            v-if="editingCommentId === comment.uuid"
            class="edit-mode"
          >
            <!-- Editable validation buttons if comment has a validation -->
            <div
              v-if="comment.validation"
              class="validation-edit"
            >
              <LocationValidation
                :initial="validation"
                @validation="(v) => (validation = v)"
              />
            </div>

            <textarea v-model="editedText" />

            <div class="edit-actions">
              <button
                :disabled="!editedText.trim() || updatingComment"
                @click="updateComment(comment)"
              >
                <b-spinner
                  v-if="updatingComment"
                  small
                />
                <span v-else> {{ $t("components.sidebar.TheComments.save") }}</span>
              </button>
              <button
                class="cancel-btn"
                @click="cancelEdit"
              >
                {{ $t("components.sidebar.TheComments.cancel") }}
              </button>
            </div>
          </div>


          <div
            v-else
            class="comment-text"
          >
            <span
              v-if="comment.validation"
              class="validation-display"
            >
              <b-icon-check
                v-if="comment.validation === 'approved'"
                class="validation-icon-check"
              />
              <b-icon-x
                v-if="comment.validation === 'rejected'"
                class="validation-icon-x"
              />
              {{
                $t(
                  `components.sidebar.TheComments.commentMessages.location_${comment.validation}`
                )
              }}
            </span>
            {{ translateComment(comment) }}
          </div>

          <div
            v-if="comment.canEdit"
            class="comment-options"
          >
            <b-icon-pencil
              class="edit-btn"
              @click="openEditor(comment)"
            />
            <b-icon-trash
              class="edit-btn trash"
              @click="deleteComment(comment)"
            />
          </div>
        </div>
      </div>
      <div v-else>
        {{ $t("components.sidebar.TheComments.noComments") }}
      </div>
    </div>
  </div>
</template>

<script>
import {
  getComments,
  insertComment,
  deleteComment,
  updateComment,
} from '@/../shared/API/comments'
import { format } from 'date-fns'
import { mapGetters } from 'vuex'
import { BIconTrash, BIconPencil, BIconCheck, BIconX } from 'bootstrap-vue'
import LocationValidation from './Components/LocationValidation.vue'
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue'
import { statusesWithLabelEnglish } from '@/../shared/services/statusTranslations'
import { constructValidation } from '@/helpers/comments'
import privilegesMixin from '@/mixins/common/privilegesMixin'
import { patchChargingPoint } from '@/../shared/API/chargingpoints'
export default {
  name: 'Comments',
  components: {
    BIconTrash,
    BIconPencil,
    LocationValidation,
    ConfirmationDialog,
    BIconCheck,
    BIconX,
  },
  mixins: [privilegesMixin],
  props: { chargingpoint: { type: Object, required: true } },
  data() {
    return {
      loadingComments: false,
      deletingCommentId: null,
      submittingComment: false,
      updatingComment: false,
      comments: [],
      newComment: '',
      editingCommentId: null,
      editedText: '',
      validation: null,
    }
  },
  computed: {
    ...mapGetters('currentUser', {
      currentUserID: 'getCurrentUserId',
      currentUserName: 'getCurrentUserName',
    }),
    isValidator() {
      return !!this.chargingpoint.properties.validators?.find(
        (validator) => validator.user_id === this.currentUserID,
      )
    },
    hasAlreadyValidated() {
      return !!this.comments.find(
        (comment) =>
          !!comment.validation && comment.user?.id === this.currentUserID,
      )
    },
  },
  watch: {
    chargingpoint(next, previous) {
      if (!next || next.uuid === previous?.uuid) return
      this.initComments()
    },
  },
  async created() {
    await this.initComments()
  },
  methods: {
    async initComments() {
      try {
        this.loadingComments = true
        const comments = (await getComments(this.chargingpoint.uuid)) ?? []

        this.comments = comments.map(comment => {
          return {
            ...comment,
            canEdit: (comment.user?.id === this.currentUserID && comment.type !== 'system') || this.superuser,
          }
        })
          .sort((a, b) => {
            const dateA = new Date(a.updated_at || a.created_at).getTime()
            const dateB = new Date(b.updated_at || b.created_at).getTime()
            return dateB - dateA
          })
      } catch (error) {
        console.error(error)
      } finally {
        this.loadingComments = false
      }
    },
    /**
     * `api_change_multi`: a sentence per field in metadata. A field that didn't change is missing
     * (comments from the API) or null (migrated comments), and gets no sentence; within a pair,
     * null means "none".
     */
    translateApiChanges(comment) {
      const t = (key, params) => this.$i18n.t(`components.sidebar.TheComments.commentMessages.api_change_multi${key}`, params)
      const has = value => value !== null && value !== undefined
      const orNone = value => (has(value) && value !== '' ? value : this.$i18n.t('none'))
      const status = key => (key ? this.$i18n.t(`participationPanel.options.statusStations.regularLabels.${key}`) : this.$i18n.t('none'))
      const {
        oldStatus, newStatus, processIdOld, processIdNew, isLocked, remarks,
        publicationDateOld, publicationDateNew, definitiveDateOld, definitiveDateNew,
      } = comment.metadata || {}

      const parts = [t('', { name: comment.user?.name || 'Unknown' })]
      if (has(oldStatus) || has(newStatus)) {
        parts.push(t('_status', { oldStatus: status(oldStatus), newStatus: status(newStatus) }))
      }
      if (has(processIdOld) || has(processIdNew)) {
        parts.push(t('_processId', { processIdOld: orNone(processIdOld), processIdNew: orNone(processIdNew) }))
      }
      if (has(isLocked)) {
        parts.push(t(['locked', 'yes'].includes(isLocked) ? '_locked' : '_unlocked'))
      }
      if (has(remarks)) {
        parts.push(remarks ? t('_remarks', { remarks }) : t('_remarks_removed'))
      }
      if (has(publicationDateOld) || has(publicationDateNew)) {
        parts.push(t('_publicationDate', { oldDate: orNone(publicationDateOld), newDate: orNone(publicationDateNew) }))
      }
      if (has(definitiveDateOld) || has(definitiveDateNew)) {
        parts.push(t('_definitiveDate', { oldDate: orNone(definitiveDateOld), newDate: orNone(definitiveDateNew) }))
      }
      return parts.join(' ')
    },
    translateComment(comment) {
      if (comment.message.id === 'custom') {
        return comment.message?.text || ''
      }

      if (comment.message.id === 'api_change_multi') {
        return this.translateApiChanges(comment)
      }

      return this.$i18n.t(
        `components.sidebar.TheComments.commentMessages.${comment.message.id}`,
        {
          name: comment.user?.name || 'Unknown',
          oldPhase: comment.metadata?.oldPhase === 'none' ? this.$i18n.t('none') : comment.metadata?.oldPhase,
          newPhase: comment.metadata?.newPhase === 'none' ? this.$i18n.t('none') : comment.metadata?.newPhase,
          oldStatus: comment.metadata?.oldStatus ? this.$i18n.t(`participationPanel.options.statusStations.regularLabels.${comment.metadata?.oldStatus}`) : '',
          newStatus: comment.metadata?.newStatus ? this.$i18n.t(`participationPanel.options.statusStations.regularLabels.${comment.metadata?.newStatus}`) : '',
          oldAddress: comment.metadata?.oldAddress === 'none' ? this.$i18n.t('none') : comment.metadata?.oldAddress,
          newAddress: comment.metadata?.newAddress === 'none' ? this.$i18n.t('none') : comment.metadata?.newAddress,
          source: comment.metadata?.import_source || comment.metadata?.source || 'NA',
          remarks: comment.metadata?.remarks,
          reason: comment.metadata?.reason,
          duplicate: comment.metadata?.duplicate ?? 'NA',
          oldMunicipality: comment.metadata?.oldMunicipality ?? this.$i18n.t('none'),
          newMunicipality: comment.metadata?.newMunicipality ?? this.$i18n.t('none'),
        },
      )
    }
    ,

    async updateStatusChargingPoint({ messageID }) {
      const validationComments = this.comments.filter(comment => comment.type === 'validation')
      const locationApproved = validationComments.every(comment => comment.validation === 'approved')
      const locationRejected = validationComments.every(comment => comment.validation === 'rejected')

      const payload = {
        isLocked: 'no',
        remarks: this.chargingpoint.properties.remark ?? '',
        processId: this.chargingpoint.externalProcessId
      }

      if (messageID.includes('status_auto_')) {
        payload.status = messageID.includes('rejected') ? 'rejected' : messageID.includes('mixed') ? 'suggestion' : 'definitive'
      } else if (messageID.includes('validators_changed_completed_')) {

        if (messageID.includes('unanimous') && locationApproved) payload.status = 'definitive'
        if (messageID.includes('unanimous') && locationRejected) payload.status = 'rejected'

        if (messageID.includes('not_unanimous')) payload.status = 'suggestion'

      } else if (messageID === 'status_reset_after_validation_deleted') payload.status = 'suggestion'
      else if (messageID.includes('validation_vote_updated_to_')) {
        payload.status = messageID.includes('consensus') ? 'definitive' : 'suggestion'
      }

      const chargingpoint = await patchChargingPoint({ chargingpointUuid: this.chargingpoint.uuid, payload })
      this.$store.dispatch('planmode/addOrUpdateChargingPoint', { chargingpoint })

    },
    async postSystemComments() {
      const { id, metadata } = constructValidation(this.validation, this.chargingpoint, this.deletingCommentId, this.updatingComment, this.comments)

      const payload = {
        user: {
          name: this.$auth.user.name,
          id: this.$auth.user.sub,
        },
        comment_type: 'system',
        message_type: id,
        metadata: metadata ?? {},
      }

      if (['approved', 'rejected'].includes(id)) {
        return
      }

      if (id) {
        payload.message = this.$i18n.t(
          `components.sidebar.TheComments.commentMessages.${id}`,
          'en-GB',
          {
            oldStatus: statusesWithLabelEnglish[metadata?.oldStatus],
            newStatus: statusesWithLabelEnglish[metadata?.newStatus],
          },
        )
      }

      await this.updateStatusChargingPoint({ messageID: id })

      const systemComment = await insertComment({
        chargingPointUUID: this.chargingpoint.uuid,
        payload,
      })

      if (systemComment) {
        this.comments.unshift(systemComment)
        this.newComment = ''
      }
    },

    structurePayload() {
      const trimmedComment = this.newComment.trim()
      const payload = {
        user: {
          name: this.currentUserName,
          id: this.currentUserID,
        },
      }

      if (this.isValidator && this.validation) {
        payload.validation = this.validation
        payload.comment_type = 'validation'

        const defaultMessage = this.$i18n.t(`components.sidebar.TheComments.commentMessages.location_${this.validation}_default_message`)

        if (!trimmedComment || trimmedComment === defaultMessage) {
          payload.message_type = `location_${this.validation}_default_message`
          payload.message = defaultMessage
        } else {
          payload.message_type = 'custom'
          payload.message = trimmedComment
        }
      } else {
        payload.comment_type = 'user'
        payload.message_type = 'custom'
        payload.message = trimmedComment
      }

      return payload
    },
    async submitComment() {
      if (!this.newComment.trim()) return
      try {
        const payload = this.structurePayload()

        this.submittingComment = true
        const result = await insertComment({
          chargingPointUUID: this.chargingpoint.uuid,
          payload,
        })

        if (result) {
          this.comments.unshift({
            ...result,
            canEdit: (result.user?.id === this.currentUserID && result.type !== 'system') || this.superuser,
          })
          this.newComment = ''
        }

        if (payload.comment_type === 'validation') await this.postSystemComments()

        this.$store.dispatch('planmode/addOrUpdateComments', { comments: [result] })
      } catch (error) {
        console.error('Error submitting comment:', error)
      } finally {
        this.submittingComment = false
      }
    },

    async deleteComment(comment) {
      const confirmed = await this.$refs.confirmDialogDeleteComment.open()

      if (!confirmed) return

      try {
        this.deletingCommentId = comment.uuid
        const result = await deleteComment(comment.uuid)
        if (result) {
          const idx = this.comments.findIndex((c) => c.uuid === comment.uuid)
          if (idx !== -1) {
            this.comments.splice(idx, 1)
          }
        }

        if (comment.type === 'validation') await this.postSystemComments()

        this.$store.dispatch('planmode/deleteComment', { comment: result })

      } catch (error) {
        console.error('Error deleting comment:', error)
      } finally {
        this.deletingCommentId = null
      }
    },

    async processValidation(validation) {
      if (this.isValidator && validation) {
        this.validation = validation

        const translation = this.$i18n.t('components.sidebar.TheComments.commentMessages.location_approved_default_message')

        if (this.newComment.trim() === '' && validation === 'approved') {
          this.newComment = translation
        } else if (validation === 'rejected' && this.newComment.trim() === translation) {
          this.newComment = ''
        }
      }
    },

    async updateComment(comment) {
      if (!this.editedText.trim()) return

      try {
        this.updatingComment = true

        const updatedComment = await updateComment({
          commentUUID: comment.uuid,
          payload: {
            message_type: 'custom',
            comment_type: comment.type || 'user',
            message: this.editedText.trim(),
            validation: this.validation ?? comment.validation,
            user: { name: this.$auth.user.name, id: this.$auth.user.sub },
          },
        })

        const idx = this.comments.findIndex((c) => c.uuid === comment.uuid)
        if (idx !== -1 && updatedComment) {
          this.$set(this.comments, idx, {
            ...this.comments[idx],
            ...updatedComment,
            canEdit: (updateComment.user?.id === this.currentUserID && updateComment.type !== 'system') || this.superuser,
          })
        }

        if (comment.type === 'validation') await this.postSystemComments()
        this.$store.dispatch('planmode/addOrUpdateComments', { comments: [updatedComment] })

        this.cancelEdit()
      } catch (error) {
        console.error('Error updating comment:', error)
      } finally {
        this.updatingComment = false
      }
    },
    openEditor(comment) {
      this.editingCommentId = comment.uuid
      this.editedText = comment.message.text
      this.validation = comment.validation || null
    },
    formatDate(timestamp) {
      return format(new Date(timestamp), 'dd/MM/yyyy HH:mm')
    },
    cancelEdit() {
      this.editingCommentId = null
      this.editedText = ''
      this.validation = null

    },
  },
}
</script>

<style lang="scss" scoped>
.validation-display {
  display: flex;
  align-items: center;
  gap: 0.3rem;

  .validation-icon {
    &-check {
      font-size: 1.6rem;
      color: #28a745;
    }

    &-x {
      font-size: 1.5rem;

      color: #dc3545;
    }
  }
}

.comments-container {
  .comment-input {
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;

    textarea {
      padding: 0.5rem;
      border-radius: 6px;
      border: 1px solid #ccc;
      resize: vertical;
      min-height: 60px;
      margin-bottom: 0.5rem;
      font-size: 0.9rem;
    }

    button {
      align-self: flex-end;
      padding: 0.5rem 1rem;
      background: #007bff;
      color: white;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      transition: background 0.2s;

      &:disabled {
        background: #999;
        cursor: not-allowed;
      }

      &:hover:not(:disabled) {
        background: #0056b3;
      }
    }
  }

  .comment-item {
    position: relative;
    padding: 0.75rem;
    margin-bottom: 0.5rem;
    background: #f9f9f9;
    border-radius: 6px;
    border: 1px solid #e0e0e0;

    .comment-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 0.85rem;
      margin-bottom: 0.25rem;
      color: #555;

      .comment-date {
        font-size: 0.75rem;
        color: #aaa;
        margin-left: 1rem;
      }
    }

    .comment-text {
      font-size: 0.9rem;
      color: #333;
      white-space: pre-line;
      margin: 0.6rem;
    }

    .edit-mode {
      textarea {
        width: 100%;
        border-radius: 6px;
        border: 1px solid #ccc;
        padding: 0.5rem;
        margin-bottom: 0.5rem;
      }

      .edit-actions {
        display: flex;
        gap: 0.5rem;

        button {
          padding: 0.3rem 0.8rem;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.2s;

          &:first-child {
            background: #28a745;
            color: white;

            &:hover {
              background: #218838;
            }
          }

          &.cancel-btn {
            background: #ccc;

            &:hover {
              background: #bbb;
            }
          }
        }
      }
    }

    .comment-options {
      position: absolute;
      bottom: 0.5rem;
      right: 0.5rem;
      display: flex;
      gap: 0.5rem;

      b-icon-trash,
      b-icon-pencil {
        font-size: 1rem;
        color: #555;
        transition: color 0.2s;

        &:hover {
          color: #007bff;
        }
      }

      .edit-btn {
        :hover {
          cursor: pointer;
        }

        &.trash {
          color: #d00;
        }
      }
    }
  }
}
</style>
